import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useIsActiveTab } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import { createLazyRows, LAZY_ROW_CHANGE, type LazyRows, type ObserverFactory } from './lazyRowState'

const LazyRowsContext = createContext<LazyRows | null>(null)

export interface LazyRowScopeProps {
  children: React.ReactNode
  /** Nur für Tests: ein eigener Observer statt `IntersectionObserver`. */
  makeObserver?: ObserverFactory
}

/**
 * **Ein Bereich je Seite, in dem Zeilen erst gebaut werden, wenn man sie sieht** (#303, Runde 2).
 *
 * Hält das Budget des ersten Bildes, die gemerkten Höhen und die Observer am Scrollcontainer. Ein innerer Bereich
 * (die `TaskList` eines Abschnitts im Board) reicht nur durch: Das Budget gilt für die ganze Seite, in Dokument-
 * Reihenfolge — sonst baute jeder Abschnitt seine ersten Zeilen, auch weit unten.
 *
 * Ruht in einem verborgenen Tab: Dort meldet der Observer alles als „weit draußen“ und entlüde die ganze Liste.
 *
 * @example
 * <LazyRowScope>
 *   {segments.map(segment => <TaskList … />)}
 * </LazyRowScope>
 */
export function LazyRowScope({ children, makeObserver }: LazyRowScopeProps) {
  const outer = useContext(LazyRowsContext)
  if (outer) return <>{children}</>
  return <OwnScope makeObserver={makeObserver}>{children}</OwnScope>
}

function OwnScope({ children, makeObserver }: LazyRowScopeProps) {
  const [rows] = useState(() => createLazyRows({ makeObserver }))
  const active = useIsActiveTab()
  rows.setActive(active)
  useEffect(() => () => rows.disconnect(), [rows])
  return <LazyRowsContext.Provider value={rows}>{children}</LazyRowsContext.Provider>
}

/**
 * Ob die Zeile ihren Inhalt bauen soll. `pinned` hält sie gebaut — ausgewählt, fokussiert, aufgeklappt, gezogen,
 * ein- oder ausfahrend: Ihr Zustand ginge sonst beim Entladen verloren. Ohne umgebenden Bereich immer `true`.
 *
 * `ref` ist die Hülle der Zeile — sie bleibt in beiden Zuständen dieselbe.
 */
export function useLazyRow(id: string, ref: React.RefObject<HTMLElement | null>, pinned: boolean): { built: boolean; placeholderHeight: number } {
  const rows = useContext(LazyRowsContext)
  const [built, setBuilt] = useState(() => !rows || rows.claim(id))
  const builtRef = useRef(built)
  builtRef.current = built

  // Später dazugekommen (Budget aufgebraucht) und doch schon im Bild: vor dem ersten Malen bauen, nicht erst
  // im nächsten Frame des Observers — sonst blitzte ein leerer Platz auf.
  useLayoutEffect(() => {
    if (!rows || builtRef.current) return
    const el = ref.current
    const root = el ? rows.root(el) : null
    if (!el || !root) return
    const box = el.getBoundingClientRect()
    const view = root.getBoundingClientRect()
    if (view.height === 0) return
    if (box.bottom >= view.top - view.height && box.top <= view.bottom + view.height) setBuilt(true)
    // Nur beim Einhängen — danach entscheidet der Observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  useEffect(() => {
    const el = ref.current
    if (!rows || !el) return
    // Nur bei echtem Wechsel: Der Observer meldet auch, was schon gilt — ein `setState` je Meldung renderte die Zeile neu.
    return rows.observe(el, id, zone => {
      const next = zone === 'near'
      if (builtRef.current !== next) setBuilt(next)
    })
  }, [rows, id, ref])

  const shown = !rows || built || pinned
  const shownBefore = useRef(shown)
  useLayoutEffect(() => {
    if (shownBefore.current === shown) return
    shownBefore.current = shown
    ref.current?.dispatchEvent(new Event(LAZY_ROW_CHANGE, { bubbles: true }))
  }, [shown, ref])

  return { built: shown, placeholderHeight: rows ? rows.height(id) : 0 }
}
